import { useCallback, useEffect, useRef, useState } from 'react'
import { profileApi } from '../api/endpoints'
import { useAuth } from '../auth/AuthContext'

/**
 * Where the map should open.
 *
 * <p>On first open this tries the browser's own geolocation first, silently - no button, no
 * waiting for a click. A live fix is the truest answer to "who is near me right now", and most
 * people's saved location is wherever they happened to be at sign-up, not where they are today.
 * A GPS fix here is used only for this map session; it is never written back to the profile; see
 * {@link requestCurrentLocation} below for the same rule on the manual retry.
 *
 * <p>If geolocation is unavailable, denied, or times out, this falls back to the server's stored
 * coordinates, filling in the same gap the old pincode-only flow did: a participant with a pincode
 * and no coordinates is placed by the server from their locality and pincode - the locality's own
 * centre when it can be found inside that pincode, so one pincode's people spread over its
 * neighbourhoods, else the pincode's centre - the map centred there, and the result written back
 * to their profile so it costs one lookup ever, not one per visit. That written-back value is a
 * genuine saved location (not a live fix), so it is saved like the rest of the profile.
 *
 * <p>`source` says which of the two is behind the current centre, so the page can offer a toggle:
 * {@link requestCurrentLocation} to switch to a live fix, {@link requestSavedLocation} to switch
 * back - the second is the only way back to the saved view once a live fix has replaced it, since
 * nothing here re-tries geolocation on its own once `centre` is set.
 */
export function useMapCentre(): {
  centre: { lat: number; lng: number } | null
  /** True while the initial auto-locate, or a manual request below, is in flight. */
  locating: boolean
  /**
   * Which request {@link locating} refers to - 'gps' for {@link requestCurrentLocation}, 'saved'
   * for {@link requestSavedLocation}, null when neither is running. Lets each button show its own
   * "Locating..." only for the request it actually triggered, including a retry that doesn't
   * change `source` (asking for a live fix again while already showing one, say) - `source` alone
   * can't tell that apart from nothing being in flight at all.
   */
  locatingFor: 'gps' | 'saved' | null
  /** Which kind of location the current `centre` came from - null before either has resolved. */
  source: 'gps' | 'saved' | null
  /** Manual retry for a live fix, e.g. a "Use my location" button. Never writes to the profile. */
  requestCurrentLocation: () => void
  /** Switches back to the saved/pincode location, e.g. a "Based on my saved location" button. */
  requestSavedLocation: (silent?: boolean) => void
  /** Set only by the two requests above; the silent initial attempt never surfaces one. */
  locationError: string | null
} {
  const { profile, refreshProfile } = useAuth()
  const [centre, setCentre] = useState<{ lat: number; lng: number } | null>(null)
  const [locatingFor, setLocatingFor] = useState<'gps' | 'saved' | null>(null)
  const [source, setSource] = useState<'gps' | 'saved' | null>(null)
  const [locationError, setLocationError] = useState<string | null>(null)

  // The profile a request was made for, checked again once its async work lands - switching
  // profile (e.g. switchProfile mid-lookup) must not let a stale pincode resolution land on
  // whichever profile happens to be current by then, local or on the server (the lookup ends in a
  // write - profileApi.updateOwn).
  const latestProfileRef = useRef(profile)
  useEffect(() => {
    latestProfileRef.current = profile
  }, [profile])

  // `silent` distinguishes the initial effect's own fallback call (nothing to show for a miss, and
  // nothing earned the right to interrupt with an error) from a manual click (which did, and gets
  // told when it comes up empty).
  const requestSavedLocation = useCallback((silent = false) => {
    if (!profile) return
    const requestedFor = profile
    if (!silent) setLocationError(null)
    // Already has coordinates: use them and do nothing else.
    if (profile.lat != null && profile.lng != null) {
      setCentre({ lat: profile.lat, lng: profile.lng })
      setSource('saved')
      return
    }
    if (!profile.pincode) {
      if (!silent) {
        setLocationError('No saved location on your profile yet. Add a pincode or use your current location.')
      }
      return
    }

    setLocatingFor('saved')
    profileApi
      .locationSuggestion()
      .then(async (found) => {
        if (latestProfileRef.current !== requestedFor) return
        if (!found) {
          if (!silent) setLocationError('Could not work out a location from your pincode.')
          return
        }
        setCentre(found)
        setSource('saved')
        // Written back so this happens once. Failing to save is not worth surfacing - the map is
        // already centred, and the next visit simply looks it up again.
        await profileApi
          .updateOwn({ lat: found.lat, lng: found.lng })
          .then(() => refreshProfile?.())
          .catch((err) => console.error('[map-centre] could not save the pincode location', err))
      })
      .catch((err) => {
        console.error('[map-centre] could not resolve the pincode', err)
        if (latestProfileRef.current === requestedFor && !silent) {
          setLocationError('Could not load your saved location. Please try again.')
        }
      })
      .finally(() => {
        if (latestProfileRef.current === requestedFor) setLocatingFor(null)
      })
  }, [profile, refreshProfile])

  useEffect(() => {
    if (!profile || centre) return
    let cancelled = false

    if (!navigator.geolocation) {
      requestSavedLocation(true)
      return
    }

    setLocatingFor('gps')
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (cancelled) return
        setLocatingFor(null)
        setCentre({ lat: pos.coords.latitude, lng: pos.coords.longitude })
        setSource('gps')
      },
      () => {
        if (cancelled) return
        setLocatingFor(null)
        // Denied, unavailable, or timed out - fall back rather than leaving the map blank. Silent,
        // same as the rest of this first attempt: a permission prompt the person never asked for is
        // not the moment to also show an error about it.
        requestSavedLocation(true)
      },
      { timeout: 8000 },
    )

    return () => {
      cancelled = true
    }
    // requestSavedLocation deliberately excluded: it changes identity with every profile refresh
    // (e.g. the very save it triggers), which would otherwise re-arm this effect and re-request
    // geolocation on a loop. `profile` alone is enough to run it once per freshly loaded session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, centre])

  function requestCurrentLocation() {
    if (!navigator.geolocation) {
      setLocationError('This browser cannot share your location. Try a different browser, or search by pincode instead.')
      return
    }
    setLocatingFor('gps')
    setLocationError(null)
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocatingFor(null)
        setSource('gps')
        // Session-only, like the initial attempt above: this does not overwrite the saved profile
        // location, so a one-off "where am I right now" never clobbers the address on file.
        setCentre({ lat: pos.coords.latitude, lng: pos.coords.longitude })
      },
      (err) => {
        setLocatingFor(null)
        setLocationError(
          err.code === err.PERMISSION_DENIED
            ? 'Location access was denied. Allow it in your browser\'s site settings, or search by pincode instead.'
            : 'Could not get your current location. Check your device\'s location settings and try again.',
        )
      },
      { timeout: 8000 },
    )
  }

  return {
    centre,
    locating: locatingFor !== null,
    locatingFor,
    source,
    requestCurrentLocation,
    requestSavedLocation,
    locationError,
  }
}
